import React, { useState, useEffect } from 'react';
import { Award, Users, CheckCircle, Clock, AlertTriangle, TrendingUp, Loader2 } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useNavigate } from 'react-router-dom';

export default function SkillsTrainingWidget() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dashboardData = await apiRequest('/skills/dashboard');
        setData(dashboardData);
      } catch (error) {
        console.error('Failed to fetch skills dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-[#DC2626]" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="h-full">
        <CardContent className="py-8 text-center text-slate-500">
          Unable to load skills data
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full" data-testid="skills-training-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Award className="h-4 w-4 text-purple-500" />
            Skills & Training
          </span>
          <button 
            onClick={() => navigate('/admin/skills-matrix')}
            className="text-xs text-[#DC2626] hover:underline"
          >
            View All
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-slate-600">Staff</span>
            </div>
            <p className="text-xl font-bold text-slate-900 mt-1">{data.total_staff}</p>
          </div>

          <div className="p-3 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm text-slate-600">Skill Coverage</span>
            </div>
            <p className="text-xl font-bold text-slate-900 mt-1">{data.skill_coverage}%</p>
          </div>
        </div>

        {/* Certification Compliance */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Cert Compliance</span>
            <span className="text-sm font-medium">{data.certification_compliance}%</span>
          </div>
          <Progress 
            value={data.certification_compliance} 
            className="h-2"
          />
        </div>

        {/* Training Completion */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Training Completion</span>
            <span className="text-sm font-medium">{data.training_completion}%</span>
          </div>
          <Progress 
            value={data.training_completion} 
            className="h-2"
          />
        </div>

        {/* Alerts */}
        <div className="flex flex-wrap gap-2">
          {data.expiring_certifications > 0 && (
            <Badge 
              className="bg-amber-100 text-amber-700 cursor-pointer hover:bg-amber-200"
              onClick={() => navigate('/admin/certifications')}
            >
              <Clock className="h-3 w-3 mr-1" />
              {data.expiring_certifications} Expiring
            </Badge>
          )}
          {data.expired_certifications > 0 && (
            <Badge 
              className="bg-red-100 text-red-700 cursor-pointer hover:bg-red-200"
              onClick={() => navigate('/admin/certifications')}
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              {data.expired_certifications} Expired
            </Badge>
          )}
          {data.staff_incomplete_mandatory > 0 && (
            <Badge 
              className="bg-purple-100 text-purple-700 cursor-pointer hover:bg-purple-200"
              onClick={() => navigate('/admin/training-progress')}
            >
              <TrendingUp className="h-3 w-3 mr-1" />
              {data.staff_incomplete_mandatory} Incomplete Training
            </Badge>
          )}
        </div>

        {/* Quick Links */}
        <div className="pt-2 border-t flex gap-2">
          <button 
            onClick={() => navigate('/admin/skills-matrix')}
            className="flex-1 text-xs py-2 px-3 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 transition-colors"
          >
            Skills Matrix
          </button>
          <button 
            onClick={() => navigate('/admin/certifications')}
            className="flex-1 text-xs py-2 px-3 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 transition-colors"
          >
            Certifications
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
